import React, { forwardRef, MouseEventHandler, ReactNode } from 'react';
import { omit } from 'lodash-es';
import { Drag16 } from '@honeybook/icons';
import HBText from '../HBText/HBText';
import { HBHighlightText } from '../HBHighlightText';
import HBCheckbox from '../HBCheckbox/HBCheckbox';
import HBFlex from '../HBFlex/HBFlex';
import HBBox from '../HBBox/HBBox';
import { useListNavigationContext } from '../common/ListNavigationContext';

import { HBListItemProps } from './HBListItem.types';
import {
    ListItemContainer,
    ListItemSideElement,
    ListItemHoverAction,
    ListItemInnerContainer,
    ListItemSelectionIndicator,
    ListItemDragHandle
} from './HBListItem.styles';

import {
    CONTAINER_PADDING_X,
    INNER_CONTAINER_PADDING_X,
    SELECTED_BG_COLOR,
    SIZE_TO_HEIGHT,
    SIZE_TO_PADDING,
    THEME
} from './HBListItem.constants';
import HBActionArea, { useActionArea } from '../accessibility/HBActionArea';
import { useTestId } from '../../hooks/useTestId';
import { vars } from '../../theme';

const CheckIcon = ({ disabled }: { disabled: boolean }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="10"
        height="10"
        viewBox="0 0 10 10"
        fill="none"
    >
        <path
            d="M1 5.80012L4.2 9.00012L9 1.00012"
            stroke={disabled ? 'currentColor' : vars.colors.text.primary}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const HBListItem = forwardRef<HTMLDivElement, HBListItemProps>(
    (
        {
            prefix,
            suffix,
            textSuffix,
            hoverAction,
            subtext,
            children,
            selected,
            selectable,
            showDragHandle,
            dragHandleProps,
            theme = 'neutral',
            size = 'md',
            rounded = true,
            disabled = false,
            id,
            onClick,
            highlightTextProps,
            isFullWidthContent,
            'data-testid': dataTestId = 'hb-list-item',
            ...props
        },
        ref
    ) => {
        const { highlightSubtext = false, ...highlightTextPropsInternal } =
            highlightTextProps || {};
        const getTestId = useTestId(dataTestId);
        const { enabled: isNavigableList, suppressItemTabStop } =
            useListNavigationContext();
        const _theme: keyof typeof THEME = disabled ? 'disabled' : theme;
        const currTheme = THEME[_theme];
        const textType = currTheme.textTypes[size];
        const textColor = currTheme.textColor;

        /**
         * Renders content with optional highlighting.
         * Only applies highlighting if:
         * 1. highlightTextProps is provided with a query
         * 2. content is a string (not ReactNode)
         * Highlights children and subtext, but not textSuffix (typically metadata)
         */
        const renderContent = (content: ReactNode): ReactNode => {
            if (
                typeof content !== 'string' ||
                !highlightTextPropsInternal ||
                !highlightTextPropsInternal.query
            ) {
                return content;
            }

            return (
                <HBHighlightText {...highlightTextPropsInternal}>
                    {content}
                </HBHighlightText>
            );
        };

        const { actionAreaProps, containerProps } =
            useActionArea<HTMLButtonElement>({
                enabled: Boolean(onClick),
                id,
                onClick:
                    onClick as unknown as MouseEventHandler<HTMLButtonElement>,
                disabled
            });

        return (
            <ListItemContainer
                displayName="ListItemContainerFlex"
                ref={ref}
                position="relative"
                paddingX={CONTAINER_PADDING_X}
                backgroundColor="surface.item"
                width="100%"
                maxWidth="100%"
                minWidth="150px"
                theme={_theme}
                data-list-item
                data-disabled={disabled ? 'true' : null}
                data-interactive={containerProps['data-interactive'] ?? null}
                data-focus-visible={
                    containerProps['data-focus-visible'] ?? null
                }
                data-selected={selected || null}
                data-draggable={showDragHandle || null}
                data-size={size}
                data-rounded={rounded || null}
                {...(isNavigableList && {
                    role: 'option',
                    ...(selectable !== undefined && {
                        'aria-selected': !!selected
                    })
                })}
                onClick={e => {
                    e.stopPropagation();
                    actionAreaProps.ref?.current?.click();
                }}
                {...getTestId()}
                {...omit(props, 'data-disabled')}
            >
                {onClick && (
                    <HBActionArea
                        {...actionAreaProps}
                        {...getTestId('action-area')}
                        {...(isNavigableList
                            ? { tabIndex: -1, 'aria-hidden': true }
                            : suppressItemTabStop
                            ? { tabIndex: -1 }
                            : selected !== undefined && {
                                  'aria-pressed': selected
                              })}
                    />
                )}
                <ListItemInnerContainer
                    displayName="ListItemInnerContainerBox"
                    width="100%"
                    maxWidth="100%"
                    borderRadius={rounded ? 'xs' : undefined}
                    backgroundColor={
                        selected && _theme !== 'disabled'
                            ? SELECTED_BG_COLOR
                            : undefined
                    }
                    {...getTestId('inner-container')}
                    {...omit(containerProps, 'data-disabled')}
                >
                    <HBFlex
                        displayName="ListItemInnerContainerFlex"
                        gap="8"
                        paddingX={INNER_CONTAINER_PADDING_X}
                        paddingY={SIZE_TO_PADDING[size]}
                        minHeight={`${
                            SIZE_TO_HEIGHT[size][
                                subtext ? 'enlargedSize' : 'defaultSize'
                            ]
                        }px`}
                    >
                        {showDragHandle && (
                            <ListItemDragHandle
                                displayName="ListItemDragHandleFlex"
                                flexShrink="0"
                                padding="0"
                                margin="0"
                                {...dragHandleProps}
                                disabled={disabled}
                                onPointerDown={e => {
                                    e.stopPropagation();
                                    dragHandleProps?.onPointerDown?.(e);
                                }}
                                onMouseDown={e => {
                                    e.preventDefault();
                                    dragHandleProps?.onMouseDown?.(e);
                                }}
                                {...getTestId('drag-handle')}
                            >
                                <Drag16 />
                            </ListItemDragHandle>
                        )}
                        {selectable === 'multi' && (
                            <HBFlex
                                displayName="ListItemCheckboxSlotFlex"
                                flexShrink="0"
                                pointerEvents="none"
                                aria-hidden="true"
                                {...getTestId('checkbox')}
                            >
                                <HBCheckbox
                                    checked={!!selected}
                                    disabled={disabled}
                                    size="sm"
                                    testIdName="list-item-checkbox"
                                    onChange={() => {
                                        // no-op: selection is handled by the
                                        // list item's onClick. This placeholder
                                        // silences React's controlled-input warning.
                                    }}
                                    tabIndex={-1}
                                />
                            </HBFlex>
                        )}
                        {prefix && (
                            <HBFlex
                                displayName="ListItemSideElementFlex"
                                position="relative"
                                {...getTestId('prefix')}
                            >
                                <ListItemSideElement flexShrink="0">
                                    {prefix}
                                </ListItemSideElement>
                            </HBFlex>
                        )}
                        <HBFlex
                            displayName="ListItemContentWrapper"
                            justify="flex-start"
                            gap="4"
                            flex="1"
                            {...getTestId('content-wrapper')}
                        >
                            <HBFlex
                                displayName="ListItemContentFlex"
                                direction="column"
                                width={isFullWidthContent ? '100%' : undefined}
                                {...getTestId('content')}
                            >
                                <HBText
                                    type={textType}
                                    color={textColor}
                                    id={`${containerProps.id}-content`}
                                >
                                    {renderContent(children)}
                                </HBText>
                                {subtext && (
                                    <HBText
                                        type={textType}
                                        color={
                                            disabled
                                                ? 'text.disabled'
                                                : 'text.tertiary'
                                        }
                                    >
                                        {highlightSubtext
                                            ? renderContent(subtext)
                                            : subtext}
                                    </HBText>
                                )}
                            </HBFlex>
                            {textSuffix && (
                                <HBBox
                                    displayName="ListItemTextSuffix"
                                    alignSelf="flex-start"
                                    {...getTestId('text-suffix')}
                                >
                                    {textSuffix}
                                </HBBox>
                            )}
                        </HBFlex>
                        {hoverAction && (
                            <ListItemHoverAction
                                displayName="ListItemHoverActionBox"
                                flexShrink="0"
                                {...getTestId('hover-action')}
                            >
                                {hoverAction}
                            </ListItemHoverAction>
                        )}
                        {suffix && (
                            <HBFlex
                                displayName="ListItemSideElementFlex"
                                flexShrink="0"
                                position="relative"
                                {...getTestId('suffix')}
                            >
                                {suffix}
                            </HBFlex>
                        )}
                        {selectable === 'single' && (
                            <ListItemSelectionIndicator
                                displayName="ListItemSelectionIndicatorBox"
                                width="2px"
                                height="16px"
                                flexShrink="0"
                                {...getTestId('selection-indicator')}
                            >
                                {selected && <CheckIcon disabled={disabled} />}
                            </ListItemSelectionIndicator>
                        )}
                    </HBFlex>
                </ListItemInnerContainer>
            </ListItemContainer>
        );
    }
);

export default HBListItem;
